import { Box, Typography, useTheme, TextField } from "@mui/material";
import { useNavigate }   from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState }      from "react";
import moment            from "moment";
import * as yup          from "yup";

import ReceiptLongIcon   from "@mui/icons-material/ReceiptLong";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import CancelIcon        from "@mui/icons-material/Cancel";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import useListDateFilter from "hooks/useListDateFilter";
import FixedSizeDialog   from "components/fixedSizeDialog";
import TopSnack          from "components/TopSnack";
import { currencyFormatter }      from "utilities/currencyFormatter";
import { useGetReceiptsList }     from "api/hooks/billing";
import { requester }              from "api/pesabackend";
import { setRefreshCount }        from "state/authSlice";

const receiptCancelSchema = yup.object().shape({
  receipt_id:           yup.number().required("required"),
  cancellation_reason:  yup.string().required("required"),
});

export default function Receipts({ setIsSuccess }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    receiptsList, count, isLoadingReceiptsList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange,
  } = useGetReceiptsList(1, business_branch_id);

  const [search, setSearch] = useState("");

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });
  const [open,          setOpen]          = useState(false);
  const [cancelDetails, setCancelDetails] = useState({ receipt_id: "", receipt_number: "", cancellation_reason: "" });
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [errors,        setErrors]        = useState({});
  const [openSnack,     setOpenSnack]     = useState({ open: false, message: "", severity: "" });

  const filtered = receiptsList.filter((r) =>
    r.receipt_number?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenDialog = (row) => {
    setCancelDetails({ receipt_id: row.id, receipt_number: row.receipt_number, cancellation_reason: "" });
    setErrors({});
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setCancelDetails({ receipt_id: "", receipt_number: "", cancellation_reason: "" });
    setErrors({});
    setOpen(false);
  };

  const handleChangeCancelData = (e) => {
    const { value } = e.target;
    setCancelDetails((prev) => ({ ...prev, cancellation_reason: value }));
    if (!value.trim()) {
      setErrors((prev) => ({ ...prev, cancellation_reason: "This field is required" }));
    } else {
      setErrors((prev) => ({ ...prev, cancellation_reason: "" }));
    }
  };

  const handleSubmitCancel = async () => {
    try {
      await receiptCancelSchema.validate(cancelDetails, { abortEarly: false });
    } catch (error) {
      const newErrors = {};
      error.inner.forEach((e) => { newErrors[e.path] = e.message; });
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      await requester.post("/billing/receipts/cancel", cancelDetails);
      setOpenSnack({ open: true, message: "Receipt cancelled successfully", severity: "success" });
      dispatch(setRefreshCount());
      handleCloseDialog();
    } catch {
      setOpenSnack({ open: true, message: "Operation failed", severity: "error" });
    }
    setIsSubmitting(false);
  };

  const columns = [
    { label: "#",           width: 40  },
    { label: "Date Created"            },
    { label: "Receipt No."             },
    { label: "Invoice No."             },
    { label: "Amount",   align: "right"},
    { label: "Customer"                },
    { label: "Created By"              },
    { label: "Actions",  align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "0.5rem 0.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <ReceiptLongIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Receipts
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {count} total records
            </Typography>
          </Box>
        </Box>
      </Box>

      <ListFilterBar>
        <ListDateFilterBar
          embedded
          dateFilter={displayDateFilter}
          customRange={customRange}
          onFilterChange={handleFilterChange}
          onCustomRangeChange={setCustomRange}
          onCustomApply={handleCustomApply}
        />
        <ListSearchField placeholder="Search receipt number…" onSearch={setSearch} />
      </ListFilterBar>

      {/* ── Table ── */}
      <ReportTable
        columns={columns}
        rows={filtered}
        loading={isLoadingReceiptsList}
        hasMore={hasMore}
        onLoadMore={loadMore}
        loadingMore={isLoadingMore}
        emptyMessage="No receipts found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>{moment(row.date_created).format("LLL")}</RCell>
            <RCell>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {row.receipt_number}
              </Typography>
            </RCell>
            <RCell>{row.related_invoice.invoice_number}</RCell>
            <RCell align="right">
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                {currencyFormatter(parseFloat(row.amount_paid))}
              </Typography>
            </RCell>
            <RCell>
              {row.related_invoice.related_customer
                ? `${row.related_invoice.related_customer.related_user.first_name} ${row.related_invoice.related_customer.related_user.last_name}`
                : <Typography sx={{ fontSize: "12px", color: textSec }}>—</Typography>}
            </RCell>
            <RCell>{`${row.created_by.first_name} ${row.created_by.last_name}`}</RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                <Box
                  onClick={() => navigate("/invoiceDetails", { state: { id: row.related_invoice.id } })}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    px: "8px", py: "4px", borderRadius: "6px",
                    border: "1px solid rgba(29,158,117,0.3)",
                    cursor: "pointer", color: accent,
                    fontSize: "12px", fontWeight: 500,
                    transition: "all 0.15s",
                    "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" },
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: "14px" }} /> View
                </Box>
                <Box
                  onClick={() => handleOpenDialog(row)}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "4px",
                    px: "8px", py: "4px", borderRadius: "6px",
                    border: "1px solid rgba(226,75,74,0.3)",
                    cursor: "pointer", color: "#e24b4a",
                    fontSize: "12px", fontWeight: 500,
                    transition: "all 0.15s",
                    "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" },
                  }}
                >
                  <CancelIcon sx={{ fontSize: "14px" }} /> Cancel
                </Box>
              </Box>
            </RCell>
          </RRow>
        )}
      />

      {/* ── Cancel dialog ── */}
      <FixedSizeDialog
        open={open}
        handleClose={handleCloseDialog}
        dialogHeading={`Cancel Receipt ${cancelDetails.receipt_number}`}
        negativeResponse="Revert"
        positiveResponse="Yes, Cancel Receipt"
        handleSubmit={handleSubmitCancel}
        isSubmitting={isSubmitting}
        isSubmittingText="Cancelling receipt, please wait…"
        maxWidth="sm"
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* Warning notice */}
            <Box sx={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              p: "0.875rem 1rem",
              backgroundColor: "rgba(226,75,74,0.07)",
              border: "1px solid rgba(226,75,74,0.2)",
              borderRadius: "8px",
            }}>
              <CancelIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0, mt: "1px" }} />
              <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>
                You are about to cancel receipt <strong>{cancelDetails.receipt_number}</strong>. This action cannot be undone. Please provide a reason below.
              </Typography>
            </Box>

            {/* Reason input */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for cancellation"
              value={cancelDetails.cancellation_reason}
              onChange={handleChangeCancelData}
              name="cancellation_reason"
              error={Boolean(errors.cancellation_reason)}
              helperText={errors.cancellation_reason}
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: "13px",
                  backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#fafafa",
                  borderRadius: "8px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: errors.cancellation_reason
                    ? "#e24b4a"
                    : isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)",
                },
                "& .MuiInputLabel-root": { fontSize: "13px" },
                "& .MuiFormHelperText-root": { fontSize: "11px" },
              }}
            />
          </Box>
        }
      />

      <TopSnack
        open={openSnack.open}
        handleCloseSnack={(_, reason) => { if (reason !== "clickaway") setOpenSnack({ ...openSnack, open: false }); }}
        message={openSnack.message}
        severity={openSnack.severity}
      />
    </Box>
  );
}